<!--

Copyright Paul B. Lucas (C) 2025   ALL RIGHTS RESERVED.
Licensed to the University of Illinois Urbana Champaign

This component is rendered when an employee user selects the VIEW MY AGREEMENTS route on the initial splash page.  
Agreements are listed with significant properties in a table row format if the @media probe returns a screen size > MOBILE.  
If MOBILE size is detected, then agreement 'rows' are rendered in  a box container with alternating label:value 
rows in a single column.   Actions are rendered as buttons - three to a column.


Revisions:
    02/25/2025      pblucas     initial creation
     -->
<template>
    <!-- 
        My Agreements are based on a call to employee on credentials passed
        in claims which returns the primary key for the employee, if valid.
        PK is used to return all matching agreements on fk_employee_id in 
        the agreement object 
    
        Render switched will render either ViewAgreementEntire on a specific
        agreement in list, edit the agreement in list,  or render the entire list on a 
        Boolean render state.  {'LIST', 'EDIT',"VIEW"}
    -->

    <div class="_master_container">
        <div class="_container_1_0" v-if="renderState == 'THANKS'">
        <!-- thank you message -->
            <h1 class="_h1_00">Thank You </h1>
            <p class="_p_00">
                You have submitted your loan request on {{ agreement['date-submitted'] }}.  You can review your agreements at any time by 
                clicking 'My Agreements' in the above navigation bar.  Your request for an equipment loan has recorded.  

            </p>
            <div class="_agreements_controls">
                <button class="_main_button"   type="button" v-if="renderState == 'THANKS'"
                    @click="backToList(viewIndex)"
                    @keydown.enter="backToList(viewIndex)">Continue
                </button>
            </div>    
        </div>
        <div class="_container_1_0" v-if="renderState == 'INVALID'">
            <!-- invalid submittal message -->
            <h1 class="_h1_00">Sorry - Your Submittal is Invalid </h1>
            <p class="_p_00">
                You have attempted to submit your equipment loan agreement, however you have not consented to the agreement terms.
                You must check the boxes in the 'User Compliance and Consent' section of the agreement in order to submit the request.
            </p>
            <div class="_agreements_controls">
                <button class="_main_button"   type="button" v-if="renderState == 'INVALID'"
                    @click="saveAndRedit(viewIndex)"
                    @keydown.enter="saveAndRedit(viewIndex)">Continue
                </button>
            </div>    
        </div>
        <div class="_container_1_0" v-if="this.renderState == 'EDIT-PROHIBITED'">
            <h1 class="_h1_00">Your Cannot Edit an Already Submitted Agreement</h1>
            <p class="_p_00">
                You have attempted to edit an agreement which has been already recorded.  If you wish to change your loan agreement
                you must resubmit the request.
            </p>
            <div class="_agreements_controls">
                <button class="_main_button"   type="button" v-if="renderState == 'EDIT-PROHIBITED'"
                    @click="backToList()"
                    @keydown.enter="backToList">Continue
                </button>
            </div>    
        </div>
        <div v-if="renderState=='LIST'">
            <div v-if="agreements?.length  && agreements.length !== 0">
                <div class="_list">
                    <p class="_p_list_header">Agreements for {{ agreements[0]['employee-name'] }}</p>
                </div>
                <div class="_list_container">
                    <div class="_agreements_table">

                        <div class="_agreements_table_row">
                            <div class="_agreements_table_cell">
                                <p class="_p_list_header">Agreement Number</p>
                            </div>
                            <div class="_agreements_table_cell">
                                <p class="_p_list_header">Validity Term</p>
                            </div>
                             <div class="_agreements_table_cell">
                                <p class="_p_list_header">Date Submitted</p>
                            </div>
                            <div class="_agreements_table_cell">
                                <p class="_p_list_header">&nbsp;</p>
                            </div>
                            <div class="_agreements_table_cell">
                                <p class="_p_list_header">&nbsp;</p>
                            </div>
                             <div class="_agreement_actions_table_cell">
                                <!-- agreement type /hybrid, in-state, in-usa -->
                                <p class="_p_list_actions_header">Actions</p>
                            </div>
                        </div>
                    
                        <div class="_agreements_table_row" v-for="(agree,idx) in agreements" >                                                      
                            <div class="_agreements_table_link_cell" style="text-align: left;"
                                aria-label="Link to indexed agreement"   
                                aria-description="lickable link to view agreement indexed in table"  
                                @click="viewAgreement(idx)"   
                                @keydown.enter="viewAgreement(idx)">
                                {{ agreements[idx]['number'] }}
                            </div>                
                            <div class="_agreements_table_cell" style="text-align: left;">
                                {{ agreements[idx]['validity-period'] }} months
                            </div>                
                            <div class="_agreements_table_cell" v-if="agreements[idx]['date-submitted'].length > 0">
                                {{ prettyDate(agreements[idx]['date-submitted']) }}
                            </div>
                            <div class="_agreements_table_cell" v-else>
                                <p class="_p_cell_entry">Not submitted </p>
                            </div>
                            <div class="_agreements_table_cell">
                                <p class="_p_list_header">&nbsp;</p>
                            </div>
                            <div class="_agreements_table_cell">
                                <p class="_p_list_header">&nbsp;</p>
                            </div>
 
                            <div class="_agreement_actions_table_cell">
                                <button class="_action_button" style="order:1;" @click.stop="templateAgreement(idx)">Use As Template</button>
                                <button class="_action_button" style="order:2;" @click.stop="viewAgreement(idx)">View </button>
                                <button class="_action_button" style="order:3;" @click.stop="editAgreement(idx)">Edit</button>

                            </div>
                           
                        </div>
                    </div>  <!-- end of table -->
                </div>      <!-- end of bordered container-->
            </div>
            <div v-else>
                <p class="_agreements_warning">
                    You have no agreements saved or in effect
                </p>
            </div>      
        </div>      <!-- end of list render -->
        <!--  agreements?.[viewIndex] &&  -->
        <div v-if="renderState=='EDIT'">
            <!--  edit the agreement selected on idx -->
            <div class="_saved_right" v-if="savedFlag">
                <p class="_p_saved">Agreement Saved</p>
            </div>
            <div class="_p_templated" v-if="templatedFlag">
                <p class="_p_template">
                    Using Agreement Number {{ templatedAgreeNumber }} as a Model
                </p>
            </div>
            <LoanForm :agreement="agreement" 
                  :employee="user" 
                  :issuedAssets="issuedAssets"
                  :claims="claims"  
                  :gat="gat"
                  @homeAddressRequested="setRemoteAddressToHome" />  
          
        </div>

        <div v-if="agreements?.[viewIndex] && renderState=='VIEW'">
            <!-- render the ViewAgreementEntire View on selected agreement -->
            
           <ViewAgreementEntire :agreement = "agreements[viewIndex]" 
                                :key = "agreements[viewIndex].uuid"
                                :issuedAssets="issuedAssets"
                                :claims="claims"  
                                :gat="gat"
                                :employee="user" 
                                :config="config"/> 

        </div>
        <div class="_flag_container" v-if="savedFlag">
            <p class="_p_flags">Saved Agreement</p>
        </div>
        <div class="_agreements_controls">
            
            <button class="_main_button"   type="button" v-if="renderState == 'VIEW' || renderState == 'EDIT'"
                @click="backToList()"
                @keydown.enter="backToList">Back to List</button>
        
            <button class="_main_button" type="button" v-if="(agreements.length > 1) && (renderState == 'VIEW' || renderState == 'EDIT')"
                @click="nextAgreement()"
                @keydown.enter="nextAgreement()">Next </button>
        
            <button class="_main_button" type="button" v-if="renderState == 'VIEW'"
                @click="generatePDF()"
                @keydown.enter="generatePDF">Create a PDF </button>

            <button class="_main_button" type="button" v-if="renderState == 'EDIT'"
                @click="saveAgreement(viewIndex)"
                @keydown.enter="saveAgreement(viewIndex)">Save (for later)</button>

            <button class="_main_button" type="button" v-if="renderState == 'EDIT'"
                @click="cancel(viewIndex)"
                @keydown.enter="cancel(viewIndex)">Cancel </button>

            <button class="_main_button" type="button" v-if="renderState == 'EDIT'"
                @click="reviewAgreement(viewIndex)"
                @keydown.enter="reviewAgreement(viewIndex)">Review </button>

            <button class="_main_button" type="button" v-if="renderState == 'EDIT'"
                @click="submitAgreement(viewIndex)"
                @keydown.enter="submitAgreement(viewIndex)">Submit </button>
            
        </div>
        <!--   breaks to allow footer space -->
        <br/><br/>
    </div>
    
</template>

<script>
    import {ref, computed}              from 'vue'
    import {useStore}                   from 'vuex'

    import ViewAgreementEntire          from '@/components/Agreements/ViewAgreementEntire.vue'
    import LoanForm                     from '@/components/Agreements/LoanForm.vue'

    // import composables
    import useAgreementsAPI             from '@/composables/useAgreementsAPI'
    import useAssetsAPI                 from '@/composables/useAssetsAPI'
    import useDateHelpers               from '@/composables/useDateHelpers'
    import useRefreshAPI                from '@/composables/useRefreshAPI'
    import usePermissionsAPI            from '@/composables/usePermissionsAPI'

    // pdf converter  install as npm install --save dom-to-pdf
    import domToPdf                     from 'dom-to-pdf'

    // import enumerations
    import SysConfigs                   from '@/configuration/form-options.json'

    export default {
        components: {
            ViewAgreementEntire,
            LoanForm
        },
        props: [
          'claims',
          'gat'
        ],

        emits: [
            'homeAddressRequested'
        ],
    
        setup(props)    {
            
            const { agreeResults, fetchAgreementsOnQuery } = useAgreementsAPI()
            const { assetResults, assetKeyData, fetchAssetsOnQuery, fetchAssetsOnOrgs, storeAsset} = useAssetsAPI()

            const store                         =   useStore()
            const { checkStoreForRefresh }      =   useRefreshAPI()
            const { checkAdminGroups }          =   usePermissionsAPI()
  
            //  store data
            const user                  =   computed( () => store.state.user)
            const config                =   computed( () => store.state.config)
 
            // these need to store the user also

            const sysConfigs            =   ref([])
            sysConfigs.value            =   SysConfigs

            const agreements            =   ref([])
            const agreement             =   ref({})
            const issuedAssets          =   ref([])
            const backupAgreement       =   ref({})                     // for cancel operations
            const copiedAgreement       =   ref({})
            const templatedAgreeNumber  =   ref(null)

            const viewIndex             =   ref(0)        

          
            // MARK:  refresh store from database if global browser refresh fubars the store
            checkStoreForRefresh()
            .then( () => {
                fetchAgreementsOnQuery({"employee-fk": user.value.id})
                .then( () => {
                    agreements.value = agreeResults.value 

                })
            })
            checkAdminGroups(props.claims)

 
            return {user, config, agreements, agreement, copiedAgreement, backupAgreement, 
                    sysConfigs, viewIndex,  templatedAgreeNumber, issuedAssets 
                   }
        },

        data() {
 
            return {
                // switch for page conditional rendering        
                renderState: 'LIST',
                savedFlag: false,
                templatedFlag: false
            }
        },
        methods: {
              // FUNCTION: Invoked as emit from RWA Form (required to mutate employee in store under strict protocol)
            // Argument aContact is the contact object
            addContactToEmployee(aContact) {
                //console.log("RRW:: addContactToEmployee fired")
                const addContact        = (contact)  =>  this.$store.dispatch('addContact', contact)
                addContact(aContact)
            },
            // FUNCTION: Invoked as emit from RWA Form (required to mutate employee in store under strict protocol)
            // Argument is index into contacts array on this.employee (in store:  state.user.contacts)
            deleteContactFromEmployee(idx) {
                const deleteContact  = (idx)  =>  this.$store.dispatch('deleteContact', idx)
                deleteContact(idx)
            },
            setRemoteAddressToHome()  {
                this.agreements[this.viewIndex]['asset-address1']    = this.user?.['address1'] ?? ""
                this.agreements[this.viewIndex]['asset-address2']    = this.user?.['address2'] ?? ""
                this.agreements[this.viewIndex]['asset-city']        = this.user?.['city']     ?? ""
                this.agreements[this.viewIndex]['asset-state']       = this.user?.['state']    ?? ""
                this.agreements[this.viewIndex]['asset-postcode']    = this.user?.['postcode'] ?? ""
                this.agreements[this.viewIndex]['asset-country']     = this.user?.['country']  ?? "United States"
            },
            
            // FUNCTION: converts an ISO date (YYYY-MM-DDTHH:MM:sSS.SSSZ) to human readable
            prettyDate(date)     {
                const { makePrettyDate}      =   useDateHelpers()
                return makePrettyDate(date)        
            },
            //  >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
            //  MARK:  - THE FOLLOWING ARE ACTION METHODS ASSOCIATED WITH THE LISTED ACTION BUTTONS FOR A GENERAL USER
            //  >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

            // FUNCTION: takes the agreement on the list using the v-for indices and renders entire form
            // Function is member of action list with (agree, idx) argument
            // Note: We only want to view loaned assets here so filter to eliminaet issued but not loaned
            viewAgreement(idx) {

                // housework
                this.templatedFlag = false;
                this.viewIndex = idx

                // set agreement and copy (non-reactive) for backup operation
                this.agreement          =   this.agreements[idx]
                this.issuedAssets = JSON.parse(JSON.stringify(this.agreement['loaned-assets']))

                this.backupAgreement    =   JSON.parse(JSON.stringify(this.agreements[idx]))
                this.renderState        =   "VIEW"
            },

            // FUNCTION: takes agreement from selected row and applies to form component
            // Function is member of action list with (agree, idx) argument
            // Edits are disallowed if agreement has been submitted (users cannot revoke loans once made)
            //
            async editAgreement(idx) {

                const { assetResults, fetchAssetsOnQuery } = useAssetsAPI()

                this.viewIndex = idx
               
                // set agreement and copy (non-reactive) for backup operation
                this.agreement          =   this.agreements[idx] 

                // check for whether edits are allowed - e.g. not  yet submitted
                if(this.agreements[idx]['date-submitted'].length != 0) {
                    this.renderState = 'EDIT-PROHIBITED'
                }
                else {
                    this.backupAgreement    =   JSON.parse(JSON.stringify(this.agreements[idx]))

                    // agreements have assets list, but we want to first list all assets issued to employee,
                    // then mark as selected any assets on the agreement itself.  Then user can deselect as
                    // part of edit, if desired.

                    let agreementCheckedAssets = []
                    await fetchAssetsOnQuery({"steward-netid": this.user.netid, "status": "Active-Issued"})
                    .then( async () => {
                        // assets issued to employee not previously loaned/checked as loan
                        // add selection property to array
                        this.issuedAssets = assetResults.value.map(o => ({...o, ...{"selected": false}}))
                        // fetch assets issues marked as selected (for loan)
                        await fetchAssetsOnQuery({"steward-netid": this.user.netid, "status": "Active-Loaned"})
                        .then ( () =>   { 
                            agreementCheckedAssets = assetResults.value.map(o => ({...o, ...{"selected": true}}))
                            // transfer them to the issued list with the selection prop
                            agreementCheckedAssets.forEach( (a) =>  {
                                this.issuedAssets.push(a)
                            })
                        })
                                          
                    })
                    // bump against the current agreement asset list to see if assets are marked loaned or not
                    this.renderState        =   "EDIT"
                }
            },
             // FUNCTION: copies selected agreement from v-for row into new copiedAgreement,
            //            and clears dates.  Builds new number, clears UUID,
            //            and stores new agreement for pending submittal retaining all  
            //            agreement properties (assets)
            //
            //            Pushes agreement into edit mode
            //
            //  Function is member of action list with (agree, idx) argument
            //
            async templateAgreement(idx) {

                const {agreeResults, agreeKeyData, fetchAgreementsOnQuery, storeAgreement} = useAgreementsAPI()

                let templateDate = new Date()

                // copy agreement data and reset unique values
                this.copiedAgreement     = JSON.parse(JSON.stringify(this.agreements[idx]) ) 
                
                // reset uuid - forces insert rather than update in database
                this.copiedAgreement.uuid = ""
                // change state to initial state
                this.templatedAgreeNumber = this.copiedAgreement.number
                this.copiedAgreement.number = ""

                // reset dates
                this.copiedAgreement['date-submitted']    =   ""

                // reset all consents
                this.copiedAgreement['policy-consent']              =   false
                this.copiedAgreement['security-consent']            =   false
                let oldNotes = this.copiedAgreement['agreement-notes']
                this.copiedAgreement['agreement-notes']   = "Templated from " + this.templatedAgreeNumber + " on " 
                        + this.prettyDate(templateDate) +" \n"
                this.copiedAgreement['agreement-notes']  += oldNotes
                 
 
                this.agreement = this.copiedAgreement
                this.backupAgreement    =   JSON.parse(JSON.stringify(this.agreement))
                this.renderState    =   'EDIT'
                this.templatedFlag  =   true
 
            },


            //  >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
            //  MARK:  THE FOLLOWING METHODS ARE PER LIST ELEMENT ACTION METHODS APPLICABLE ONLY TO ADMINISTRATIVE USERS
            //         AND MAY NOT BE APPLICABLE TO CASES WHERE A SPECIFIC USER'S AGREEMENTS ARE LISTED
            //  >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

            // FUNCTION: Disregards any edits and retores the agreement to a new agreeement (PENDING) state
            //
            cancel(idx)   {
                // return form to pristine state and disregard edits
                this.agreements[idx]    =   this.backupAgreement 
                this.viewIndex          =   idx
                this.templatedFlag      =   false;
                this.renderState        =   "LIST"
            },
           
            // FUNCITON - Saves an agreement when editing - no submissison
            // Assumes this.agreement is set to agreement edited
            async saveAgreement(idx)   {
                const {agreeResults, agreeKeyData, fetchAgreementsOnQuery, storeAgreement} = useAgreementsAPI()

                // clear loaned assets array on agreement
                this.agreements[idx]['loaned-assets'] = []

                // push assets marked to loan agreement issued assets array
                this.issuedAssets.forEach( (a) => {
                    if(a.selected) {
                        a.status = 'Active-Loaned'    
                    }
                    else {
                        a.status = 'Active-Issued'
                    }
                    this.agreements[idx]['loaned-assets'].push(a)
                   
                })
                // default validity term
                if(this.agreements[idx]['validity-period'].length == 0  )
                    this.agreements[idx]['validity-period'] = 24
              
                await storeAgreement(this.agreements[idx])
                .then(async () => {
                    await fetchAgreementsOnQuery({id: agreeKeyData.value.results[0].pkey})
                    .then( () => {
                        this.agreements[idx]      = agreeResults.value[0]    
                        this.renderState        =   "EDIT"
                        this.templatedFlag      =   false;
                        this.savedFlag          =   true
                       
                    })
                })    
            },   
            // Recovery from invalud submittal - save existing changed and redit
            async saveAndRedit(idx) {
                await this.saveAgreement(idx)
                .then( () => {
                    this.savedFlag      =   false
                })
                
            },
            // FUNCTION:  To review an edited form in a concise manner - essentially same as viewAgreement except
            //            agreement is saved first to obtain UUID.
            //
            reviewAgreement(idx)   {
                const {agreeResults, agreeKeyData, fetchAgreementsOnQuery, storeAgreement} = useAgreementsAPI()

                // invokes full view or agreement and employee
                // Note: Since some agreement and employee properties are constructed by the database constructor
                // on storeObject(), we need to first store then refetch the object.  Must be done as a promise

                this.agreement = this.agreements[idx]
                this.viewIndex = idx

                // modify the viewed assets to loaned only
                
                this.issuedAssets = JSON.parse(JSON.stringify(this.agreement['loaned-assets']))
                this.agreement['loaned-assets'] = []
 
                // push assets marked to loan agreement issued assets array
                this.issuedAssets.forEach( (a) => {
                    if(a.selected) {
                        a.status = 'Active-Loaned'    
                    }
                    else {
                        a.status = 'Active-Issued'
                    }
                    this.agreements[idx]['loaned-assets'].push(a)
                   
                })

                storeAgreement(this.agreement)
                .then( async () => {
                    await fetchAgreementsOnQuery({id: agreeKeyData.value.results[0].pkey})
                    .then( () => {
                        //console.log("Fetched Agreement: "+JSON.stringify(agreeResults.value[0]))
                        this.agreements[idx]      =   agreeResults.value[0]                
                        this.renderState    =   'VIEW'
                        this.templatedFlag  =   false;
                    })
                })
            },
            // FUNCTION:  allows user to submit pending agreements 
            //            
            async submitAgreement()   {

                const {agreeResults, agreeKeyData, fetchAgreementsOnQuery, storeAgreement} = useAgreementsAPI()

                if( this.agreement['agreement-consent']  == false  ||
                    this.agreement['security-consent']   == false  ||
                    this.agreement['loaned-assets'].length == 0 ) {

                    this.renderState    =   "INVALID"
                    return
                }

                // clear loaned assets array on agreement
                this.agreement['loaned-assets'] = []

                // push assets marked to loan agreement issued assets array
                this.issuedAssets.forEach( (a) => {
                    if(a.selected) {
                        a.status = 'Active-Loaned'    
                    }
                    else {
                        a.status = 'Active-Issued'
                    }
                    this.agreement['loaned-assets'].push(a)
                
                })
                // default validity term
                if(this.agreement['validity-period'].length == 0  )
                    this.agreement['validity-period'] = 24
                
                this.viewIndex      =   0
                this.savedFlag      =   false
                this.templatedFlag  =   false
                this.renderState    =   "LIST"
              
                await storeAgreement(this.agreement)
                .then(async () => {
                    fetchAgreementsOnQuery({"employee-fk": this.user.id})
                    .then( () => {
                        this.agreements         =   agreeResults.value 
                        this.renderState        =   "THANKS"
                        this.templatedFlag      =   false;
                        this.savedFlag          =   false
                    })       
                })
            },

             // MARK:  THE FOLLOWING METHODS ARE APPLIED TO VIEW/EDIT ELEMENTS TO RETURN TO OVERALL LIST - GENERAL USERS
            
             backToList() {
                const {agreeResults, agreeKeyData, fetchAgreementsOnQuery, fetchAgreementsOnOrgs, storeAgreement} = useAgreementsAPI()
              
                // need to reload list as there may be new copies
                fetchAgreementsOnQuery({'employee-fk': this.user.id})
                .then( () => {
                    this.agreements     =   agreeResults.value 
                    this.viewIndex      =   0
                    this.renderState    =   "LIST"
                    this.templatedFlag  =   false;

                })   
            },
            // FUNCTION: increments through viewing index (experimental)
            nextAgreement() {

                // cleanup from possible templating
                this.templatedFlag      =   false;

                this.viewIndex++
                if (this.viewIndex >= this.agreements.length) this.viewIndex = 0
            },
            // FUNCTION: Created a PDF of ViewAgreementEntire rendering
            // id of element on DOM is '_pdf_render'
            // Need to created from the DOM and then do a standard download so that the PDF appears
            // in the browser downloads.
            async generatePDF() {
                
                // MARK:  generate the appropriate filename with embedded date
                const date = new Date()
                let year    =   date.getFullYear().toString()
                let month   =   date.getMonth()+1
                month       =   month.toString().padStart(2,'0')
                let day     =   date.getDate()
                day         =   day.toString().padStart(2,'0')
                let hours   =   date.getHours().toString().padStart(2,'0')
                let mins    =   date.getMinutes().toString().padStart(2,'0')

                 // 20240404_1559 format
                const filedate = year+month+day+'_'+hours+mins
                const pdfFileName = "WorkLoan-Agreement-"+"-"+filedate+".pdf"    

                // select and the container and add a title for teh PDF document              
                var element = document.getElementById('_pdf_render');
                var title   = document.createElement("p")
                var brk     = document.createElement("br")
                title.textContent = "Equipment Loan Agreement"
                title.style['font-family'] = "'Times New Roman',Times, serif"
                title.style['font-size'] = "16pt"
                title.style['font-weight'] = "bold"
                title.style['text-align'] = "center"
                title.appendChild(brk)
                title.appendChild(brk)
                var firstChild = document.getElementById("_pdf_row1")
                element.insertBefore(title,firstChild)

                var options = {
                    filename: pdfFileName,
                    excludeTagNames: ['button', 'textarea']
                };
                await domToPdf(element, options, function(pdf) {
                     
                    title.remove()
                });
                
            }
        },      // end of methods list

        // hooks
        beforeMount() {

        },
        mounted() {
            //console.log("LMA Mounted:: Number of My Agreements Found: "+this.agreements.length)
            //console.log("LMA Mounted:: Agreements: "+JSON.stringify(this.agreements))
        },
        updated() {
            //console.log("LMA:: Updated: user: "+JSON.stringify(this.user))
            //console.log("LMA Update:: Number of My Agreements Found: "+this.agreements.length)
            //console.log("LMA Update:: Agreements: "+JSON.stringify(this.agreements))

        }
    }
</script>

<style scoped>

    ._master_container {
        font-family:Monserrat, Avenir, Helvetica, Arial, sans-serif;
        min-height: 50vh;
        width: 95vw;
        height: 90%;
        margin: 0 auto;
        padding: 0;
    }
    ._container_1_0 {
        width: 100%;
        margin: 0 auto; 
        padding: 25px 0;
    }
   
    ._list_container {
        width: 98%;
        overflow-x: auto;
        margin: 0 5px;;
        padding: 0 5px;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color: #BFC9D9;
    }
    ._list {
        margin: 10px 0 0 0;
        padding: 0;
    }
    ._p_list_header {
        margin: 0;
        padding: 2px 5px;
        color: #13294B;
        font-size: 0.8em;
        font-weight: bold;
        text-align: left;
    }
    ._p_list_actions_header {
        margin: 0 auto;
        padding: 2px 0;
        color: #13294B;
        font-size: 0.9em;
        font-weight: bold;
        text-align: center;
    }
    /* list action button styling */
    ._action_button {
        border-style: solid;
        border-color: #13294B;
        border-width: 1px;
        border-radius: 4px;
        background-color:#13294B;
        color: white;
        margin: 0 5px;
        padding: 0 3px;
        font-size: 0.8em;
    }
    ._action_button:hover {
        background-color: #FF5F05;
        color: white;
    }
 
   
    ._agreements_controls {
        display: flex;
        align-items: enter;
        justify-content: center;
        margin: 0;
        padding: 5px;
    }
    ._agreements_inline_controls {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0;
        padding: 5px;
    }
    /* agreements list styling */
    ._agreements_table {
        display: table;
    }

    
    ._agreements_table_row   {
        background-color:#BFC9D9;
        display: table-row;
        font-size: 0.8em;
        font-weight: normal;
        color: black;
        overflow: hidden;
        height: 25px;
        white-space: nowrap;
    }

    ._agreements_table_row:nth-child(2n+2) {
        display: table-row;  
        text-align: center;
        margin: 0;
        background-color:#a4b9dc;
        font-size: 0.8em;
        font-weight: normal;
        color: black;
    }

 
    ._agreements_table_cell  {
        display: table-cell;
        min-width: 10%;
        text-align: left;
        margin: 0;
        padding: 2px 0 2px 10px;
    }
    ._agreement_actions_table_cell {
        display: flex;
        min-width: 20%;
        flex-direction: row;
        flex-wrap: nowrap;
        align-content: center;
        justify-content: center;
        /* justify-content:space-between;*/
        margin: 0 0 0 10px;
    }
    ._agreements_table_link_cell  {
        display: table-cell;
        min-width: 40px;
        text-align: left;
        color: #13294B;
        text-decoration: none;
        margin: 0 0 0 20px;
        padding: 2px 0 2px 10px;
    }
    ._agreements_table_link_cell:hover  {
        display: table-cell;
        min-width: 40px;
        text-align: left;
        color: #FF5F05;
        text-decoration: none;
        margin: 0 0 0 20px;
        padding: 2px 0 2px 10px;
    }
    ._p_column_headers {
        font-size: 1.0em;
        font-weight: bold;
        color: #13294B;
        text-align: center;
        margin: 2px 0;
        padding: 5px;
    }
    ._h1_00 {
        font-family:Monserrat, Avenir, Helvetica, Arial, sans-serif;
        color: #FF5F05;
        font-size: 16pt;
        text-align: center;
    }
    ._p_00 {
        max-width: 100%;
        text-align: justify;
        margin: 2px auto;
        border: 1px;
        border-color: black;
    }
    ._p_cell_entry {
        text-align: left;
        margin: 0;
        padding: 0 2ps;
        color: black;
    }
    ._agreements_warning {
        text-align: center;
        color: #FF5F05;
        margin: 25px auto;
        padding: 5px;
    }
    ._flag_container {
        width: 15%;
        padding: 5px;
        float: right;
    }
    ._p_flags {
        font-size: 14pt;
        font-weight: bold;
        color: #FF5F05;
    }
    /* general controls styling */
    ._main_button {
        background-color: #13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    ._main_button:hover {
        background-color: #FF5F05;
        color: white;
    }
    
    .button_edit_1 {
        background-color: #13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-width: 2px;
        border-radius: 5px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    .button_edit_1:hover {
        background-color: #FF5F05;
        color: white;
    }
    ._p_saved {
        font-size: 16pt;
        color: #FF5F05;
        padding: 2px;
    }
    ._saved_right {
        padding: 0;
        margin: 0 10px 0 0;
        float: right;
    }
    ._p_templated    {
        padding: 0;
        margin: 0;
    }
    ._p_template {
        margin: 5px auto 5px;
        padding: 0;
        color: #13294B;
        font-size: 12pt;
    }
    @media screen and (max-width: 800px) {
        ._agreements_controls {
            width: 50%;  
            margin: 0;
        }
    }

</style>